import { useState } from "react";
import { Users, UserPlus } from "lucide-react";
import type { PlanTraveler } from "@/lib/types";
import TripTravelersDialog from "./TripTravelersDialog";

/* Compact traveler-names row for the itinerary header (R3). Shows WHO is on the
   trip — the owner ("You") plus any named companions — as small chips. When the
   trip's passenger COUNT exceeds the people we can name, it shows an "+N more"
   affordance so a "2 travelers" trip never renders with nobody shown. The chip /
   button opens the real add-to-trip flow (share/invite by email, per-traveler
   role). */

interface TravelerNamesProps {
  travelers: PlanTraveler[] | null | undefined;
  /** The trip these travelers belong to — enables the add/manage dialog. */
  tripId?: string;
  /** The trip's passenger count, so we can reconcile names vs. the total. */
  passengers?: number | null;
}

export default function TravelerNames({ travelers, tripId, passengers }: TravelerNamesProps) {
  const [open, setOpen] = useState(false);
  const named = (travelers ?? []).filter((t) => t && t.name && t.name.trim());
  const canManage = !!tripId;

  // How many travelers are on the trip but not yet named (e.g. "2 passengers"
  // with only the owner named → 1 unnamed). Never negative.
  const unnamed = Math.max(0, (passengers ?? named.length) - named.length);

  // Nothing named at all and no count to reconcile → the bare "add" affordance.
  if (named.length === 0 && unnamed === 0) {
    return (
      <>
        {canManage ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex w-fit items-center gap-1 text-xs font-medium text-[hsl(var(--primary))] transition-colors hover:underline"
          >
            <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
            Add travelers
          </button>
        ) : (
          <a
            href="/travelers"
            className="inline-flex w-fit items-center gap-1 text-xs font-medium text-[hsl(var(--primary))] transition-colors hover:underline"
          >
            <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
            Add travelers
          </a>
        )}
        {canManage && <TripTravelersDialog tripId={tripId!} open={open} onOpenChange={setOpen} />}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1 font-medium">
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          Travelers
        </span>
        <ul className="flex flex-wrap items-center gap-1">
          {named.map((t) => (
            <li
              key={t.id}
              className="rounded-full bg-[hsl(var(--primary))]/[0.1] px-2 py-0.5 font-medium text-[hsl(var(--accent-foreground))]"
            >
              {t.name.trim()}
              {t.isSelf ? <span className="ml-1 font-normal text-muted-foreground">(you)</span> : null}
            </li>
          ))}
        </ul>
        {/* Reconcile the count: a "2 travelers" trip with only "You" named shows
            "+1 to add" so the extra passenger is never invisible. */}
        {canManage &&
          (unnamed > 0 ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 font-medium text-[hsl(var(--accent-foreground))] transition-colors hover:bg-[hsl(var(--primary))]/[0.08]"
            >
              <UserPlus className="h-3 w-3" aria-hidden="true" />
              {`Add ${unnamed} more`}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 font-medium text-[hsl(var(--accent-foreground))] transition-colors hover:bg-[hsl(var(--primary))]/[0.08]"
            >
              <UserPlus className="h-3 w-3" aria-hidden="true" />
              Add
            </button>
          ))}
      </div>
      {canManage && <TripTravelersDialog tripId={tripId!} open={open} onOpenChange={setOpen} />}
    </>
  );
}
